import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, PenLine } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SharedSignaturePad from "@/components/SignaturePad";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SignAddendum() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/addendum/${token}`).then(({ data }) => { setData(data); setName(data.signer_default || ""); }).catch(() => setErr("This addendum link is invalid or no longer available."));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try { await axios.post(`${BACKEND}/api/public/addendum/${token}/sign`, { signer_name: name, signature_data_url: sig, signer_title: title }); setDone(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center text-sm text-[#6C757D]" data-testid="addendum-invalid">{err}</div>;
  if (!data) return <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center text-sm text-[#6C757D]">Loading…</div>;
  const signed = done || data.dealer_signed;
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="sign-addendum-page">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">{data.party_name}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black text-[#0B132B] mt-1">{data.title || "Addendum to Dealer Agreement"}</h1>
        <p className="text-sm text-[#6C757D] mt-1">{data.dealer_name}{data.dba ? ` dba ${data.dba}` : ""}{data.physical_address ? ` · ${data.physical_address}` : ""}</p>

        <Card className="p-6 border-[#E9ECEF] rounded-sm mt-6">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B132B]">Additional terms</div>
            <a href={`${BACKEND}/api/public/addendum/${token}/addendum.pdf`} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#0B132B] underline" data-testid="addendum-view-pdf">View PDF</a>
          </div>
          <div className="whitespace-pre-wrap text-sm text-[#0B132B] bg-[#F8F9FA] border border-[#E9ECEF] rounded-sm p-4" data-testid="addendum-terms">{data.body}</div>
          <p className="text-[11px] text-[#6C757D] mt-3">This addendum supplements your Dealer Agreement with {data.party_name}. {data.counter_signed ? `${data.party_name} has signed (${data.counter_signer_name}).` : `${data.party_name} will countersign after you.`}</p>
        </Card>

        {signed ? (
          <Card className="p-10 border-green-200 bg-green-50 rounded-sm text-center mt-4" data-testid="addendum-thanks">
            <CheckCircle size={40} className="mx-auto text-green-600" />
            <div className="mt-3 text-xl font-bold text-[#0B132B]">Thank you — the addendum is signed</div>
            <p className="text-sm text-[#6C757D] mt-2">{data.counter_signed ? "This addendum is fully executed." : `${data.party_name} will countersign and the executed copy will be added to your dealer packet.`}</p>
          </Card>
        ) : (
          <Card className="p-6 border-[#0B132B]/20 rounded-sm mt-4 space-y-4" data-testid="addendum-sign-card">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11 rounded-sm" data-testid="addendum-signer-name" /></div>
              <div><Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Title / position</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Dealer Principal" className="mt-1 h-11 rounded-sm" data-testid="addendum-signer-title" /></div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-2 flex items-center gap-1.5"><PenLine size={12} /> Signature</Label>
              <SharedSignaturePad onChange={setSig} testId="addendum-signature-pad" />
            </div>
            <Button onClick={submit} disabled={busy || !name.trim() || !sig} className="w-full h-12 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold text-sm uppercase tracking-[0.14em]" data-testid="addendum-sign-submit">{busy ? "Submitting…" : "Sign addendum"}</Button>
          </Card>
        )}
      </main>
    </div>
  );
}
